import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Button, Progress, Alert, Spin, Typography, Space, Tag,
} from 'antd';
import { ReloadOutlined, InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import * as api from 'util/api';

const { Text } = Typography;

const POLL_INTERVAL_MS = 2000;

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

const Cin7Cache = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [startingSync, setStartingSync] = useState(false);
  const [lastSyncError, setLastSyncError] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const status = await api.getCin7SyncStatus();
      setSyncStatus(status);
      if (status.status === 'failed' && status.error) {
        setLastSyncError(status.error);
      }
      if (status.status === 'completed') {
        setLastSyncError(null);
      }
    } catch (err) {
      setSyncStatus({ status: 'idle', error: err.message });
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (syncStatus?.status === 'running') {
      pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
    if (syncStatus?.status === 'completed' || syncStatus?.status === 'failed') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [syncStatus?.status]);

  const handleStartSync = async (full = false) => {
    setStartingSync(true);
    setLastSyncError(null);
    try {
      const result = await api.syncCin7Products(full);
      const status = result?.status;
      if (status === 202) {
        await fetchStatus();
      } else if (status === 409) {
        setLastSyncError('A sync is already in progress.');
        await fetchStatus();
      } else if (status >= 400) {
        setLastSyncError(result?.data?.error || result?.data?.message || `Request failed (${status})`);
      }
    } catch (err) {
      setLastSyncError(err.message || 'Failed to start sync');
    } finally {
      setStartingSync(false);
    }
  };

  const isRunning = syncStatus?.status === 'running';
  const totalPages = syncStatus?.totalPages || 0;
  const page = syncStatus?.page || 0;
  const syncedCount = syncStatus?.syncedCount || 0;
  const syncMode = syncStatus?.mode;
  const progressPct = totalPages > 0 ? Math.min(100, Math.round((page / totalPages) * 100)) : 0;

  return (
    <div style={{ maxWidth: 640 }}>
      <Card title="Cin7 product cache" extra={<Text type="secondary">Product Database</Text>}>
        <Alert
          message="Rate limit: 60 requests per 60 seconds"
          description="The Cin7 (DEAR) API allows 60 calls per minute. Sync runs in the background with ~1 request per second. Do not start multiple syncs."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        {loadingStatus ? (
          <Spin />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space wrap>
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={() => handleStartSync(false)}
                loading={startingSync}
                disabled={isRunning}
              >
                {isRunning ? 'Syncing…' : 'Sync Cin7 products'}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => handleStartSync(true)}
                loading={startingSync}
                disabled={isRunning}
              >
                Full resync
              </Button>
            </Space>

            {isRunning && (
              <div>
                {syncMode && (
                  <Tag color={syncMode === 'incremental' ? 'blue' : 'orange'} style={{ marginBottom: 8 }}>
                    {syncMode === 'incremental' ? 'Incremental sync' : 'Full sync'}
                  </Tag>
                )}
                <Text type="secondary">
                  Page
                  {' '}
                  {page}
                  {' '}
                  (
                  {syncedCount}
                  {' '}
                  products)
                </Text>
                <Progress percent={progressPct} status="active" style={{ marginTop: 8 }} />
              </div>
            )}

            {syncStatus?.status === 'completed' && (
              <Alert
                message="Sync complete"
                description={(
                  <>
                    {syncMode && (
                      <Tag color={syncMode === 'incremental' ? 'blue' : 'orange'} style={{ marginRight: 8 }}>
                        {syncMode === 'incremental' ? 'Incremental' : 'Full'}
                      </Tag>
                    )}
                    {syncedCount}
                    {' '}
                    products synced in
                    {' '}
                    {syncStatus.totalPages}
                    {' '}
                    {syncStatus.totalPages === 1 ? 'page' : 'pages'}
                    . Completed at
                    {' '}
                    {formatTime(syncStatus.completedAt)}
                  </>
                )}
                type="success"
                showIcon
              />
            )}

            {syncStatus?.status === 'failed' && (
              <Alert
                message="Sync failed"
                description={syncStatus.error || lastSyncError || 'Unknown error'}
                type="error"
                showIcon
              />
            )}

            {syncStatus?.startedAt && (
              <Text type="secondary">
                Last run started:
                {' '}
                {formatTime(syncStatus.startedAt)}
                {syncStatus.completedAt && (
                  <>
                    {' '}
                    · Completed:
                    {' '}
                    {formatTime(syncStatus.completedAt)}
                  </>
                )}
              </Text>
            )}
          </Space>
        )}
      </Card>
    </div>
  );
};

export default Cin7Cache;
